import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, IndianRupee, Users, TrendingUp, Copy, Check, Loader2, Wallet, Share2, MessageCircle, Link2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import SEO from "@/components/SEO";

export function AffiliateLanding() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ payout_method: "upi", upi: "", account_holder: "", account_number: "", ifsc_code: "", bank_name: "" });

  useEffect(() => {
    if (!user) return;
    api.get("/affiliate/me").then(({ data }) => setMe(data)).catch(() => {});
  }, [user]);

  const enroll = async () => {
    if (!user) { nav("/login?next=/affiliate"); return; }
    setLoading(true);
    try {
      const payout_details = form.payout_method === "upi"
        ? { upi: form.upi }
        : { account_holder: form.account_holder, account_number: form.account_number, ifsc_code: form.ifsc_code, bank_name: form.bank_name };
      await api.post("/affiliates/register", { payout_method: form.payout_method, payout_details });
      const { data } = await api.get("/affiliate/me");
      setMe(data);
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  if (me) return <AffiliateDashboard me={me} />;

  return (
    <div data-testid="affiliate-landing">
      <SEO
        title="Affiliate Program"
        description="Earn 20% commission on every paid NakshaCart plan you refer. Share your link, track clicks, get paid via UPI or bank transfer."
        path="/affiliate"
      />
      <div className="bg-gradient-to-b from-emerald-50/40 to-white border-b border-slate-200">
        <div className="nc-container py-12 md:py-16 max-w-4xl">
          <span className="nc-chip"><Sparkles className="w-3.5 h-3.5" /> NakshaCart Affiliate Program</span>
          <h1 className="mt-4 text-3xl md:text-5xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: "Poppins" }}>Earn 20% commission on every paid plan you refer.</h1>
          <p className="mt-4 text-slate-600 text-lg leading-relaxed max-w-2xl">Share your unique referral link. When a builder or homeowner buys a NakshaCart plan within 90 days of clicking, you earn a flat 20% — credited automatically to your dashboard.</p>
        </div>
      </div>

      <div className="nc-container py-12 max-w-5xl">
        <div className="grid md:grid-cols-3 gap-5">
          <Card icon={Users} title="Refer" sub="Share your unique link with builders, homeowners or your network." color="emerald" />
          <Card icon={TrendingUp} title="Track" sub="Cookies are valid for 90 days. We attribute paid orders to you automatically." color="sky" />
          <Card icon={IndianRupee} title="Earn" sub="Flat 20% commission per paid plan. Withdraw anytime via UPI or bank transfer." color="emerald" />
        </div>

        <div className="mt-12 bg-white rounded-2xl border border-slate-200 nc-soft-shadow p-6 md:p-8">
          <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>Become an affiliate</h2>
          {!user && <p className="mt-2 text-sm text-slate-600">You'll need a NakshaCart account first. <Link to="/signup" className="text-emerald-700 font-medium">Create one</Link> or <Link to="/login?next=/affiliate" className="text-emerald-700 font-medium">login</Link>.</p>}
          {user && (
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Payout method</label>
                <select className="nc-input mt-1" value={form.payout_method} onChange={e => setForm({ ...form, payout_method: e.target.value })} data-testid="affil-payout-method">
                  <option value="upi">UPI</option>
                  <option value="bank">Bank Transfer</option>
                </select>
              </div>
              {form.payout_method === "upi" ? (
                <div>
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">UPI ID</label>
                  <input className="nc-input mt-1" placeholder="yourname@oksbi" value={form.upi} onChange={e => setForm({ ...form, upi: e.target.value })} data-testid="affil-upi" />
                </div>
              ) : (
                <>
                  <div><label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Account holder</label><input className="nc-input mt-1" value={form.account_holder} onChange={e => setForm({ ...form, account_holder: e.target.value })} data-testid="affil-holder" /></div>
                  <div><label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Account number</label><input className="nc-input mt-1" value={form.account_number} onChange={e => setForm({ ...form, account_number: e.target.value })} data-testid="affil-acct" /></div>
                  <div><label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">IFSC</label><input className="nc-input mt-1" value={form.ifsc_code} onChange={e => setForm({ ...form, ifsc_code: e.target.value })} data-testid="affil-ifsc" /></div>
                  <div><label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Bank name</label><input className="nc-input mt-1" value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })} data-testid="affil-bank" /></div>
                </>
              )}
              <button onClick={enroll} disabled={loading} className="sm:col-span-2 nc-btn-primary justify-center" data-testid="affil-enroll-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Activate my affiliate account
              </button>
            </div>
          )}
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-5 text-sm text-slate-600">
          <Faq q="How long do my referrals stay tracked?" a="Cookies are stored for 90 days. If your referee returns and buys within that window, you still get the commission." />
          <Faq q="When can I withdraw?" a="As soon as you have ₹500+ in available balance. Withdrawal requests must be sent from your registered email address." />
          <Faq q="Is there a self-referral check?" a="Yes. We block commissions on orders placed by you under your own affiliate code." />
          <Faq q="What if a refund happens?" a="Currently NakshaCart does not refund paid plans, so commissions are final once an order is marked paid." />
        </div>
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, sub, color }) {
  const c = color === "sky" ? "bg-sky-100 text-sky-700" : "bg-emerald-100 text-emerald-700";
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${c}`}><Icon className="w-5 h-5" /></div>
      <h3 className="mt-4 font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{title}</h3>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{sub}</p>
    </div>
  );
}

function Faq({ q, a }) {
  return (
    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
      <div className="font-medium text-slate-900">{q}</div>
      <p className="mt-1 text-slate-600">{a}</p>
    </div>
  );
}

export function AffiliateDashboard({ me: initialMe }) {
  const { user } = useAuth();
  const [me, setMe] = useState(initialMe);
  const [refs, setRefs] = useState([]);
  const [copied, setCopied] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);

  const refresh = () => {
    api.get("/affiliate/me").then(({ data }) => setMe(data));
    api.get("/affiliate/referrals").then(({ data }) => setRefs(data));
  };
  useEffect(() => { refresh(); }, []);

  if (!me) return <div className="min-h-[40vh] flex items-center justify-center"><Loader2 className="animate-spin" /></div>;

  const link = `${window.location.origin}/?ref=${me.code}`;
  const shareMessage = `Hey! Build your dream home with ready-to-use plans from NakshaCart. Use my link to get started — verified architects, BOQ + Cost Estimate included: ${link}`;
  const copy = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareMessage)}`, "_blank", "noopener");
  };
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "NakshaCart", text: shareMessage, url: link }); }
      catch { /* user cancelled */ }
    } else {
      copy();
    }
  };

  return (
    <div className="bg-slate-50/40 min-h-screen py-8" data-testid="affiliate-dashboard">
      <div className="nc-container max-w-5xl">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <span className="nc-chip"><Sparkles className="w-3.5 h-3.5" /> Affiliate · {me.commission_pct}% commission</span>
            <h1 className="mt-2 text-2xl md:text-3xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>Hello, {user?.name}</h1>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
            <div className="text-xs uppercase tracking-wider text-slate-500">Your code</div>
            <div className="font-mono font-semibold text-emerald-700 mt-0.5" data-testid="affiliate-code">{me.code}</div>
          </div>
        </div>

        <div className="mt-6 bg-gradient-to-br from-emerald-500 to-sky-600 rounded-2xl p-6 md:p-7 text-white shadow-lg">
          <div className="flex items-center gap-2 text-emerald-50">
            <Link2 className="w-4 h-4" />
            <div className="text-xs font-semibold uppercase tracking-wider">Your referral link · 90-day cookie · {me.commission_pct}% commission</div>
          </div>
          <h2 className="mt-2 text-lg md:text-xl font-semibold" style={{ fontFamily: "Poppins" }}>Share this link to start earning</h2>
          <div className="mt-4 bg-white/95 rounded-xl p-2 flex flex-wrap items-center gap-2">
            <code className="flex-1 min-w-0 px-3 py-2 text-sm text-slate-800 break-all font-mono" data-testid="affiliate-link">{link}</code>
            <button onClick={copy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition" data-testid="affiliate-copy-btn">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? "Copied" : "Copy link"}
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button onClick={shareWhatsApp} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-white text-sm font-medium transition border border-white/20" data-testid="affiliate-whatsapp-btn">
              <MessageCircle className="w-4 h-4" /> Share on WhatsApp
            </button>
            <button onClick={nativeShare} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-white text-sm font-medium transition border border-white/20" data-testid="affiliate-share-btn">
              <Share2 className="w-4 h-4" /> Share elsewhere
            </button>
          </div>
        </div>

        <div className="mt-6 grid sm:grid-cols-3 gap-4">
          <Stat label="Total earned" value={`₹${(me.balance_accrued_paise / 100).toFixed(2)}`} testid="aff-total-earned" />
          <Stat label="Available" value={`₹${(me.balance_available_paise / 100).toFixed(2)}`} testid="aff-available" highlight />
          <Stat label="Paid out" value={`₹${(me.balance_paid_paise / 100).toFixed(2)}`} testid="aff-paid" />
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <button onClick={() => setShowWithdraw(true)} disabled={me.balance_available_paise < 50000} className="nc-btn-primary" data-testid="aff-withdraw-btn">
            <Wallet className="w-4 h-4" /> Request withdrawal
          </button>
          {me.balance_available_paise < 50000 && <span className="text-xs text-slate-500 self-center">Minimum ₹500 to withdraw.</span>}
        </div>

        <h2 className="mt-10 text-lg font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>Recent referrals</h2>
        <div className="mt-3 bg-white rounded-2xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <Th>Date</Th><Th>Order amount</Th><Th>Your commission</Th><Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {refs.map(r => (
                <tr key={r.id} className="border-b border-slate-100" data-testid={`ref-row-${r.id}`}>
                  <Td>{new Date(r.created_at).toLocaleDateString()}</Td>
                  <Td>₹{(r.order_amount_paise / 100).toFixed(2)}</Td>
                  <Td className="font-semibold text-emerald-700">₹{(r.commission_paise / 100).toFixed(2)}</Td>
                  <Td><span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{r.status}</span></Td>
                </tr>
              ))}
              {refs.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-slate-400">No referrals yet. Share your link to get started.</td></tr>}
            </tbody>
          </table>
        </div>

        {showWithdraw && (
          <WithdrawModal me={me} userEmail={user?.email} onClose={() => setShowWithdraw(false)} onDone={() => { setShowWithdraw(false); refresh(); }} />
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, testid, highlight }) {
  return (
    <div className={`bg-white rounded-2xl p-5 border ${highlight ? "border-emerald-300" : "border-slate-200"}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1.5 text-2xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{value}</div>
    </div>
  );
}
const Th = ({ children }) => <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;

function WithdrawModal({ me, userEmail, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [fromEmail, setFromEmail] = useState(userEmail || "");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr("");
    const paise = Math.round(parseFloat(amount || 0) * 100);
    if (paise < 50000) { setErr("Minimum withdrawal is ₹500"); return; }
    if (paise > me.balance_available_paise) { setErr("Amount exceeds available balance"); return; }
    if (!fromEmail) { setErr("Please confirm your registered email"); return; }
    setLoading(true);
    try {
      await api.post("/affiliate/withdraw", { amount: paise, from_email: fromEmail, notes });
      alert("Withdrawal request submitted. Our team will process it within 7 working days.");
      onDone();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="withdraw-modal">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <h3 className="font-semibold text-slate-900 text-lg" style={{ fontFamily: "Poppins" }}>Request withdrawal</h3>
        <p className="text-sm text-slate-500 mt-1">Available balance: ₹{(me.balance_available_paise / 100).toFixed(2)}</p>
        <div className="mt-4 space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Amount (₹)</label>
            <input type="number" className="nc-input mt-1" value={amount} onChange={e => setAmount(e.target.value)} placeholder="500" min="500" data-testid="wd-amount" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Confirm registered email</label>
            <input type="email" className="nc-input mt-1" value={fromEmail} onChange={e => setFromEmail(e.target.value)} required data-testid="wd-email" />
            <p className="text-xs text-slate-400 mt-1">Withdrawal requests must originate from your registered email address.</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Notes (optional)</label>
            <textarea rows={2} className="nc-input mt-1" value={notes} onChange={e => setNotes(e.target.value)} data-testid="wd-notes" />
          </div>
          {err && <p className="text-sm text-rose-600">{err}</p>}
          <div className="flex gap-2 pt-2">
            <button onClick={submit} disabled={loading} className="nc-btn-primary flex-1 justify-center" data-testid="wd-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}Submit request
            </button>
            <button onClick={onClose} className="nc-btn-secondary">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
